import React, { useEffect, useState } from "react";
import { X, CalendarCheck, MapPin, Clock3, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { createBookingPaymentIntent, getBookingPayment, getSalonPublicProfile } from "../../lib/api";
import { StripePaymentForm } from "./StripePaymentForm";
import { formatAppointment } from "../../lib/datetime";

const inputClass = "w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso transition-colors text-[15px]";
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function Field({ label, testId, hint, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1.5">{label}</span>
      <input required className={inputClass} data-testid={testId} {...props} />
      {hint && <span className="block mt-1 text-[10px] text-ink/40">{hint}</span>}
    </label>
  );
}

function Confirmed({ done, onClose }) {
  return (
    <div className="text-center py-4" data-testid="booking-confirmed">
      <div className="w-14 h-14 mx-auto rounded-full bg-espresso text-bone flex items-center justify-center"><CalendarCheck size={22} strokeWidth={1.5} /></div>
      <h3 className="mt-6 font-serif text-3xl text-ink leading-tight">You&rsquo;re booked.</h3>
      <p className="mt-2 text-sm text-ink/60" data-testid="booking-confirmed-paid">${Number(done.amount).toFixed(2)} AUD secured on your card · confirmation sent to your email.</p>
      <div className="mt-6 text-left border border-sand/70 rounded-md p-4 space-y-2 text-sm text-ink/70">
        <p className="font-serif text-xl text-ink leading-tight" data-testid="booking-confirmed-service">{done.service}</p>
        <p className="flex items-center gap-2"><Clock3 size={14} className="text-mocha" />{done.appointment_at ? formatAppointment(done.appointment_at) : done.time}</p>
        <p className="flex items-center gap-2"><MapPin size={14} className="text-mocha" />{done.salon}{done.suburb ? ` · ${done.suburb}` : ""}</p>
      </div>
      <button onClick={onClose} className="mt-6 w-full rounded-full bg-espresso text-bone py-3 text-sm hover:bg-ink transition-colors" data-testid="booking-confirmed-close">Done</button>
    </div>
  );
}

export default function BookingDialog({ listing, open, onClose, prefill }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "" });
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState(null);
  const [done, setDone] = useState(null);
  const [profile, setProfile] = useState(null);
  const [consent, setConsent] = useState({ policy: false, fyllr: false, studio: false });

  useEffect(() => {
    if (!open) return;
    setDone(null); setIntent(null); setConsent({ policy: false, fyllr: false, studio: false });
    if (prefill) setForm({ name: prefill.name || "", email: prefill.email || "", mobile: prefill.phone || "" });
    if (listing?.salon_slug) { setProfile(null); getSalonPublicProfile(listing.salon_slug).then(setProfile).catch(() => setProfile({ salon_name: listing.salon, cancellation_policy: "" })); }
  }, [open, prefill, listing?.salon, listing?.salon_slug]);

  if (!open || !listing) return null;
  const policy = profile?.cancellation_policy;
  const studioName = profile?.salon_name || listing.salon;
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const tick = (key) => (event) => setConsent((prev) => ({ ...prev, [key]: event.target.checked }));

  const startPayment = async (event) => {
    event.preventDefault();
    if (policy && !consent.policy) return;
    try {
      setLoading(true);
      const res = await createBookingPaymentIntent({
        listing_id: listing.id, customer_name: form.name, customer_email: form.email, customer_mobile: form.mobile,
        origin_url: window.location.origin, fyllr_marketing_consent: consent.fyllr, studio_marketing_consent: consent.studio,
        cancellation_policy_accepted: consent.policy,
      });
      setIntent(res);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not start payment. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const confirmPaid = async () => {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const status = await getBookingPayment(intent.booking_id).catch(() => null);
      if (status?.status === "confirmed") { setDone(status); return; }
      if (status?.status === "refunded") { toast.error("This opening was taken moments ago. Your payment has been refunded in full."); onClose(); return; }
      await wait(1500);
    }
    toast.message("Payment received — we’re finalising your booking and will email you shortly.");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-espresso/30 backdrop-blur-sm px-4" onClick={onClose} data-testid="booking-dialog">
      <div className="relative bg-bone max-w-md w-full max-h-[92vh] overflow-y-auto rounded-md shadow-xl p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-sand/60 flex items-center justify-center transition-colors" data-testid="booking-dialog-close" aria-label="Close"><X size={16} strokeWidth={1.5} /></button>
        {done ? <Confirmed done={done} onClose={onClose} /> : (
          <>
            <div className="pr-6">
              <h3 className="font-serif text-2xl text-ink leading-tight" data-testid="booking-dialog-salon">{listing.salon}</h3>
              <p className="mt-1 text-sm text-ink/60">{listing.service}</p>
              <p className="mt-1 text-xs text-ink/50">{listing.appointment_at ? formatAppointment(listing.appointment_at) : listing.time} · {listing.suburb}</p>
              <p className="mt-3 font-serif text-2xl text-espresso" data-testid="booking-dialog-price">${Number(intent?.amount ?? listing.offer_price).toFixed(2)} <span className="font-sans text-xs text-ink/45">AUD</span></p>
            </div>
            {intent ? (
              <div className="mt-6" data-testid="booking-payment-step">
                <button type="button" onClick={() => setIntent(null)} className="mb-4 inline-flex items-center gap-1.5 text-xs text-ink/55 hover:text-ink" data-testid="booking-payment-back"><ArrowLeft size={12} /> Edit details</button>
                <StripePaymentForm publishableKey={intent.publishable_key} clientSecret={intent.client_secret} bookingId={intent.booking_id} amount={intent.amount} onPaid={confirmPaid} customer={{ name: form.name, email: form.email }} />
              </div>
            ) : (
              <form onSubmit={startPayment} className="mt-6 space-y-4" data-testid="booking-form">
                <Field label="Name" type="text" value={form.name} onChange={set("name")} placeholder="Your name" testId="booking-form-name" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" testId="booking-form-email" />
                <Field label="Mobile" type="tel" value={form.mobile} onChange={set("mobile")} placeholder="0400 000 000" autoComplete="tel" inputMode="tel" testId="booking-form-mobile" hint="The salon uses this to confirm your appointment." />
                <div className="border-y border-sand/70 py-4 space-y-3" data-testid="booking-consent-section">
                  {policy && <><p className="text-sm font-semibold text-ink">Cancellation policy</p><p className="text-sm leading-relaxed text-ink/65" data-testid="booking-studio-policy">{policy}</p>
                    <label className="flex items-start gap-3 text-sm text-ink/75"><input type="checkbox" checked={consent.policy} onChange={tick("policy")} className="mt-0.5 h-4 w-4 accent-espresso" data-testid="booking-policy-checkbox" /><span>I agree to {studioName}&apos;s cancellation policy.</span></label></>}
                  <label className="flex items-start gap-3 text-sm text-ink/75"><input type="checkbox" checked={consent.fyllr} onChange={tick("fyllr")} className="mt-0.5 h-4 w-4 accent-espresso" data-testid="booking-fyllr-marketing-checkbox" /><span>Keep me updated with deals and availability on Fyllr.</span></label>
                  <label className="flex items-start gap-3 text-sm text-ink/75"><input type="checkbox" checked={consent.studio} onChange={tick("studio")} className="mt-0.5 h-4 w-4 accent-espresso" data-testid="booking-studio-marketing-checkbox" /><span>Let {studioName} contact me with offers and updates.</span></label>
                </div>
                <button type="submit" disabled={loading || profile === null || (policy && !consent.policy)} className={`w-full rounded-full bg-espresso text-bone py-3 text-sm transition-colors ${loading ? "opacity-60" : "hover:bg-ink"} disabled:opacity-50`} data-testid="booking-form-submit">{loading ? "Preparing secure payment…" : "Continue to payment"}</button>
                <p className="text-[11px] text-ink/55 text-center leading-relaxed" data-testid="booking-payment-note">Your card is pre-authorised now to secure this opening and charged 2 hours before your appointment. Cancel 2+ hours ahead and you’re not charged. Secured by Stripe.</p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
